import {visit} from "unist-util-visit";
import {Root as HastRoot} from "hast";
import {h} from "hastscript";
import {DEFAULT_MEDIA_PROPERTIES} from "./constants";

export const AUDIO_TAG: string = "audio";
export const VIDEO_TAG: string = "video";
const CUSTOM_TAGS: string[] = [VIDEO_TAG, AUDIO_TAG];

/**
 * Parse custom audio/video leafs
 *
 * @param {any} node
 * @return {void}
 */
const handleCustomTags = (node: any): void => {
    const {type, name}: any = node;
    if (
        type === "containerDirective"
        || type === "leafDirective"
        || type === "textDirective"
    ) {
        if (!CUSTOM_TAGS.includes(name)) {
            return;
        }
        if (!node.data) {
            // eslint-disable-next-line no-param-reassign
            node.data = {};
        }

        const {data, attributes}: any = node;

        data.hName = name;
        // @ts-ignore
        data.hProperties = {...DEFAULT_MEDIA_PROPERTIES, ...h(name, attributes).properties};
    }
};

/**
 * Handle width, height and other HTML image properties
 *
 * @param {any} node
 * @param {number | undefined} index
 * @param {any} parent
 */
const handleImageProperties = (node: any, index: number | null, parent: any): any => {
    // TODO: Any
    if (index === null || index === undefined || node.type !== "image") {
        return;
    }

    // TODO: Any
    const next: any = parent.children[index + 1];
    if (!next || next.type !== "text") {
        return;
    }

    // TODO: Any
    const trimmed: any = next.value.trim();
    if (!trimmed.startsWith("{") || !trimmed.endsWith("}")) {
        return;
    }

    // TODO: Any
    const attrs: any = JSON.parse(trimmed);

    // eslint-disable-next-line no-param-reassign
    node.data = {
        ...node.data,
        hProperties: {
            ...(node.data?.hProperties || {}),
            ...attrs,
        },
    };

    // Remove this text node so it doesn't render
    parent.children.splice(index + 1, 1);
};

/**
 * Compute position for new leafDirective(audio, video)
 * @param {any} nodePos
 * @param {string} textBefore
 * @param {number} matchLength
 * @returns {any{}}
 */
const computePosition = (nodePos: any, textBefore: string, matchLength: number): any => {
    const lines: string[] = textBefore.split(/\r?\n/);
    const startLine: number = nodePos.start.line + lines.length - 1;
    const startColumn: number = lines.length === 1
        ? nodePos.start.column + textBefore.length
        : lines[lines.length - 1].length + 1;
    const startOffset: number = nodePos.start.offset + textBefore.length;

    const matchLines: string[] = textBefore.slice(0, matchLength).split(/\r?\n/);
    const endLine: number = startLine + matchLines.length - 1;
    const endColumn: number = matchLines.length === 1
        ? startColumn + matchLength
        : matchLines[matchLines.length - 1].length + 1;
    const endOffset: number = startOffset + matchLength;

    return {
        start: {
            line: startLine,
            column: startColumn,
            offset: startOffset,
        },
        end: {
            line: endLine,
            column: endColumn,
            offset: endOffset,
        },
    };
};

const handleCustomTagsHeadingSpaces = (node: any, index: number | null, parent: any): any => {
    if (node.type !== "text" || index === null || index === undefined) {
        return;
    }

    const {value} = node;
    // Build a regex dynamically from CUSTOM_TAGS
    const tagPattern: string = CUSTOM_TAGS.join("|"); // e.g. "video|audio|mark"
    const regex: RegExp = new RegExp(`::(${tagPattern})\\[(.*?)\\]\\{(.*?)\\}`, "g");

    let match: RegExpExecArray | null = regex.exec(value);
    // TODO: any
    const nodes: any[] = [];
    let lastIndex: number = 0;

    while (match !== null) {
        const [fullMatch, tag, text, options]: RegExpExecArray = match;

        if (match.index > lastIndex) {
            nodes.push({
                type: "text",
                value: value.slice(lastIndex, match.index),
            });
        }

        const attributes: Record<string, string> = {};
        options.split(/\s+/).forEach((pair) => {
            // TODO: no-nested-functions
            const [key, val]: string[] = pair.split("=").map((s) => s.trim());
            if (key && val) {
                // TODO: anchor-precedence
                attributes[key] = val.replace(/^"|"$/g, "");
            }
        });

        // TODO: any
        const pos: any = computePosition(node.position, value.slice(0, match.index), fullMatch.length);

        nodes.push({
            type: "leafDirective",
            name: tag,
            attributes,
            children: [{type: "text", value: text}],
            position: pos,
            data: {
                hName: tag,
                hProperties: {
                    ...(tag === AUDIO_TAG || tag === VIDEO_TAG
                        ? {controls: "controls", controlsList: "nodownload"}
                        : {}),
                    ...attributes,
                },
            },
        });

        lastIndex = regex.lastIndex;
        match = regex.exec(value);
    }

    // Push remaining text
    if (lastIndex < value.length) {
        nodes.push({
            type: "text",
            value: value.slice(lastIndex),
        });
    }

    // Replace old node with new nodes
    if (nodes.length > 0) {
        parent.children.splice(index, 1, ...nodes);
        // eslint-disable-next-line consistent-return
        return index + nodes.length;
    }
};

// remark plugin to add custom tags
const htmlDirectives = () => (tree: HastRoot) => {
    visit(tree, handleImageProperties);
    visit(tree, ["textDirective", "leafDirective", "containerDirective"], handleCustomTags);
    visit(tree, handleCustomTagsHeadingSpaces);
};

export default htmlDirectives;
